"use client";

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  type ReactNode,
} from "react";
import {
  type ConfiguratorReferenceData,
  type ConfiguratorState,
  type ConfiguratorString,
  CONFIGURATOR_INITIAL_STATE,
} from "@/types/configurator";
import { configuratorReducer } from "@/store/configurator-reducer";

// ─────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────

interface ConfiguratorContextValue {
  state: ConfiguratorState;
  referenceData: ConfiguratorReferenceData;
  setInstrument: (instrumentId: string) => void;
  setStringCount: (id: string, count: number, isNonStandard: boolean) => void;
  setScaleLength: (id: string) => void;
  setLine: (lineId: string) => void;
  setStringType: (stringTypeId: string) => void;
  setPreset: (presetId: string, strings: ConfiguratorString[], presetName?: string | null) => void;
  updateString: (position: number, updates: Partial<ConfiguratorString>) => void;
  setStrings: (strings: ConfiguratorString[]) => void;
  setColor: (colorId: string) => void;
  setStringColor: (position: number, colorId: string) => void;
  togglePerStringColor: (enabled: boolean) => void;
  resetStringColors: () => void;
  setSetName: (name: string) => void;
  setQuantity: (quantity: number) => void;
  setPrices: (unitPrice: number, totalPrice: number) => void;
  reset: () => void;
}

const ConfiguratorContext = createContext<ConfiguratorContextValue | null>(null);

export function ConfiguratorProvider({
  children,
  referenceData,
  initialState,
}: {
  children: ReactNode;
  referenceData: ConfiguratorReferenceData;
  initialState?: ConfiguratorState;
}) {
  const [state, dispatch] = useReducer(configuratorReducer, initialState ?? CONFIGURATOR_INITIAL_STATE);

  const setInstrument = useCallback((instrumentId: string) =>
    dispatch({ type: "SET_INSTRUMENT", instrumentId }), []);
  const setStringCount = useCallback((stringCountId: string, count: number, isNonStandard: boolean) =>
    dispatch({ type: "SET_STRING_COUNT", stringCountId, count, isNonStandard }), []);
  const setScaleLength = useCallback((scaleLengthId: string) =>
    dispatch({ type: "SET_SCALE_LENGTH", scaleLengthId }), []);
  const setLine = useCallback((lineId: string) =>
    dispatch({ type: "SET_LINE", lineId }), []);
  const setStringType = useCallback((stringTypeId: string) =>
    dispatch({ type: "SET_STRING_TYPE", stringTypeId }), []);
  const setPreset = useCallback((presetId: string, strings: ConfiguratorString[], presetName?: string | null) =>
    dispatch({ type: "SET_PRESET", presetId, strings, presetName }), []);
  const updateString = useCallback((position: number, updates: Partial<ConfiguratorString>) =>
    dispatch({ type: "UPDATE_STRING", position, updates }), []);
  const setStrings = useCallback((strings: ConfiguratorString[]) =>
    dispatch({ type: "SET_STRINGS", strings }), []);
  const setColor = useCallback((colorId: string) =>
    dispatch({ type: "SET_COLOR", colorId }), []);
  const setStringColor = useCallback((position: number, colorId: string) =>
    dispatch({ type: "SET_STRING_COLOR", position, colorId }), []);
  const togglePerStringColor = useCallback((enabled: boolean) =>
    dispatch({ type: "TOGGLE_PER_STRING_COLOR", enabled }), []);
  const resetStringColors = useCallback(() =>
    dispatch({ type: "RESET_STRING_COLORS" }), []);
  const setSetName = useCallback((name: string) =>
    dispatch({ type: "SET_SET_NAME", name }), []);
  const setQuantity = useCallback((quantity: number) =>
    dispatch({ type: "SET_QUANTITY", quantity }), []);
  const setPrices = useCallback((unitPrice: number, totalPrice: number) =>
    dispatch({ type: "SET_PRICES", unitPrice, totalPrice }), []);
  const reset = useCallback(() => dispatch({ type: "RESET" }), []);

  return (
    <ConfiguratorContext.Provider
      value={{
        state, referenceData, setInstrument, setStringCount, setScaleLength, setLine,
        setStringType, setPreset, updateString, setStrings, setColor, setStringColor,
        togglePerStringColor, resetStringColors, setSetName, setQuantity, setPrices, reset,
      }}
    >
      {children}
    </ConfiguratorContext.Provider>
  );
}

export function useConfigurator(): ConfiguratorContextValue {
  const ctx = useContext(ConfiguratorContext);
  if (!ctx) throw new Error("useConfigurator must be used within ConfiguratorProvider");
  return ctx;
}
